import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  COLORS,
  ChartCard,
  KpiCard,
  NotTrackedChips,
  formatNumber,
  formatPercent,
  tooltipProps,
} from '../components/ChartKit';
import { SegmentList } from './OverviewTab';

const PROVIDER_LABELS = {
  LUSHA: 'Contact enrichment (Lusha)',
  APOLLO: 'Contact enrichment (Apollo)',
  HUNTER: 'Email finder (Hunter)',
  TAVILY: 'Web research (Tavily)',
  SYSTEM: 'Platform',
};
const providerLabel = (p) => PROVIDER_LABELS[String(p || '').toUpperCase()] || p || 'Other';

/** FR-M13-006 Cost and quota analytics */
const CostTab = ({ data, periodLabel }) => {
  const providers = data.providers.map((p) => ({ ...p, label: providerLabel(p.provider) }));
  const daily = data.daily.map((d) => ({
    ...d,
    label: new Date(`${d.date}T00:00:00Z`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }),
  }));
  const usedPct = data.usedPercent ?? 0;

  return (
    <div className="analytics-tab-body">
      {data.alerts.length > 0 && (
        <div className="analytics-alerts">
          {data.alerts.map((a) => (
            <div key={a.message} className={`analytics-alert ${a.level}`} role="alert">
              <i
                className={`fas ${a.level === 'critical' ? 'fa-circle-exclamation' : 'fa-triangle-exclamation'}`}
                aria-hidden="true"
              ></i>
              {a.message}
            </div>
          ))}
        </div>
      )}

      <div className="analytics-kpis">
        <KpiCard
          icon="fa-coins"
          label="Credits used"
          value={formatNumber(data.creditsUsed)}
          hint={`${periodLabel} · ${data.scope === 'workspace' ? 'whole workspace' : 'your own usage'}`}
        />
        <KpiCard
          icon="fa-chart-line"
          label="Average per day"
          value={formatNumber(data.averageDailyCredits, 1)}
          tone="violet"
        />
        <KpiCard
          icon="fa-wallet"
          label="Credits remaining"
          value={formatNumber(data.balance)}
          hint={`of ${formatNumber(data.limit)} · ${formatPercent(data.usedPercent)} used`}
          tone={
            data.status === 'available' ? 'green' : data.status === 'almost_over' ? 'amber' : 'red'
          }
        />
        <KpiCard
          icon="fa-hourglass-half"
          label="Estimated days left"
          value={
            data.estimatedDaysRemaining === null ? '—' : formatNumber(data.estimatedDaysRemaining)
          }
          hint="At the current pace"
          tone="amber"
        />
        <KpiCard
          icon="fa-paper-plane"
          label="Emails sent"
          value={formatNumber(data.emailsSent)}
          hint={periodLabel}
          tone="slate"
        />
      </div>

      <div
        className="analytics-quota"
        role="meter"
        aria-label="Credit quota used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(usedPct)}
      >
        <div className="analytics-quota-head">
          <span>Credit quota</span>
          <span>{formatPercent(data.usedPercent)} used</span>
        </div>
        <div className="analytics-quota-track">
          <div
            className={`analytics-quota-fill ${usedPct >= 100 ? 'critical' : usedPct >= 70 ? 'warning' : ''}`}
            style={{ width: `${Math.min(100, usedPct)}%` }}
          />
        </div>
      </div>

      <div className="analytics-grid">
        <ChartCard
          wide
          title="Daily credit usage"
          subtitle={periodLabel}
          empty={!daily.length}
          emptyText="No credits were used in this period."
          table={{
            columns: ['Date', 'Credits used'],
            rows: daily.map((d) => [d.label, d.credits]),
          }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={daily} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gCredits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip {...tooltipProps} />
              <Area
                type="monotone"
                dataKey="credits"
                name="Credits used"
                stroke="#f59e0b"
                fill="url(#gCredits)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Credits by service"
          empty={!providers.length}
          table={{
            columns: ['Service', 'Credits', 'Transactions'],
            rows: providers.map((p) => [p.label, p.credits, p.transactions]),
          }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={providers}
              layout="vertical"
              margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={170} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="credits" name="Credits" radius={[0, 4, 4, 0]}>
                {providers.map((p, i) => (
                  <Cell key={p.provider} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {data.scope === 'workspace' && (
          <SegmentList
            title="Top members by credits used"
            items={data.topMembers.map((m) => ({ label: m.name, count: m.credits }))}
          />
        )}
      </div>

      <NotTrackedChips items={data.notTracked} />
    </div>
  );
};

export default CostTab;
